import { z } from 'zod';
import {
  AssessmentWizardView,
  QueryMetadata,
  type GlobalFilters,
  type QueryResult,
} from '@aurora-pvi/contracts';
import type { CandidateListRow, PagedRows } from '@aurora-pvi/testing';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';

/**
 * Classification, over HTTP.
 *
 * `listCandidates` and `getWizard` sit on the `Repositories` port; the three
 * commands do not, for the reason every other write in this app does not —
 * the port is a list of questions, and starting, scoring or finalizing an
 * assessment is not one.
 *
 * `getResult` is not here. `/v2/discover/results/:id` still reads the fixture,
 * because `AssessmentResultView` asks for a boundary register, an included
 * process list and an action-item queue that nothing in this database records
 * yet. Serving it half-real would put invented rows behind a screen whose
 * whole argument is that its rows are governed.
 */

const WizardEnvelope = z.object({ data: AssessmentWizardView, meta: QueryMetadata });

export type AssessmentWriteOutcome =
  | { ok: true; view?: QueryResult<AssessmentWizardView> }
  | { ok: false; message: string; problems?: string[] };

async function refusal(response: Response): Promise<AssessmentWriteOutcome> {
  const body = (await response.json().catch(() => ({}))) as {
    message?: string;
    problems?: string[];
  };
  return {
    ok: false,
    message: body.message ?? `Request failed (${response.status}).`,
    problems: body.problems,
  };
}

export interface AnswerDraft {
  criterionCode: string;
  score: 1 | 2 | 3 | 4 | 5 | null;
  rationale?: string | null;
  /** A citation, not a document. Creates the evidence record and links it. */
  evidence?: { title?: string; reference: string } | null;
}

export function createHttpDiscoverRepository(baseUrl: string) {
  return {
    async listCandidates(
      filters: GlobalFilters,
    ): Promise<QueryResult<PagedRows<CandidateListRow>>> {
      const response = await apiFetch(`${baseUrl}/candidates?${filterParams(filters).toString()}`);
      if (!response.ok) throw new Error(`GET /candidates failed: ${response.status}`);
      return (await response.json()) as QueryResult<PagedRows<CandidateListRow>>;
    },

    /**
     * The wizard for a candidate.
     *
     * A candidate nobody has started an assessment for is a 404, and the
     * screen offers to start one. Returning an empty wizard instead would mean
     * opening the page silently created an assessment — a write behind a read,
     * and an assessment somebody is recorded as having started when all they
     * did was look.
     */
    async getWizard(candidateId: string): Promise<QueryResult<AssessmentWizardView>> {
      const response = await apiFetch(`${baseUrl}/candidates/${candidateId}/assessment`);
      if (response.status === 404) {
        throw new Error('NoAssessment');
      }
      if (!response.ok) {
        throw new Error(`GET /candidates/${candidateId}/assessment failed: ${response.status}`);
      }
      return WizardEnvelope.parse((await response.json()) as unknown);
    },

    async startAssessment(candidateId: string): Promise<AssessmentWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/candidates/${candidateId}/assessment`, {
        method: 'POST',
      });
      if (!response.ok) return refusal(response);
      return { ok: true, view: WizardEnvelope.parse((await response.json()) as unknown) };
    },

    async saveAnswers(
      assessmentId: string,
      answers: AnswerDraft[],
    ): Promise<AssessmentWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/assessments/${assessmentId}/answers`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      if (!response.ok) return refusal(response);
      return { ok: true, view: WizardEnvelope.parse((await response.json()) as unknown) };
    },

    async finalize(assessmentId: string, rationale: string): Promise<AssessmentWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/assessments/${assessmentId}/finalize`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ rationale }),
      });
      return response.ok ? { ok: true } : refusal(response);
    },
  };
}
